import { useState, useEffect } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, Edit, Trash2, Shield, Users, Settings,
  Server, Monitor, Sparkles, ChevronDown, ChevronRight, Save, Copy
} from 'lucide-react';
import { toast } from 'sonner';

// Module icons
const moduleIcons = {
  'assets': Server,
  'micro': Monitor,
  'config': Settings,
  'admin': Shield,
  'core': Shield,
  'ai': Sparkles,
};

// Module display names
const moduleNames = {
  'assets': 'Assets',
  'micro': 'Microinformática',
  'config': 'Configuración',
  'admin': 'Administración',
  'core': 'Core',
  'ai': 'Inteligencia artificial',
};

const initialFormData = {
  role_name: '',
  description: '',
  is_active: 1,
};

const RolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  
  // CRUD State
  const [modalOpen, setModalOpen] = useState(false);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [permissionsData, setPermissionsData] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});

  const fetchRoles = async () => {
    try {
      const response = await api.get('/admin/roles');
      setRoles(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar roles');
    }
  };

  const fetchPages = async () => {
    try {
      const response = await api.get('/admin/roles/pages');
      setPages(response.data.pages || []);
      setModules(response.data.modules || {});
      // Expand all modules by default
      const expanded = {};
      Object.keys(response.data.modules || {}).forEach(m => expanded[m] = true);
      setExpandedModules(expanded);
    } catch (error) {
      console.error('Error fetching pages:', error);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchRoles(), fetchPages()]);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  // CRUD Handlers
  const handleCreate = () => {
    setEditingItem(null);
    setFormData(initialFormData);
    setModalOpen(true);
  };

  const handleEdit = (role) => {
    setEditingItem(role);
    setFormData({
      role_name: role.role_name || '',
      description: role.description || '',
      is_active: role.is_active ?? 1,
    });
    setModalOpen(true);
  };

  const handleEditPermissions = async (role) => {
    try {
      const response = await api.get(`/admin/roles/${role.id}`);
      const rolePerms = response.data.permissions || [];
      
      // Convert to map: page_id -> {can_read, can_create, can_update, can_delete}
      const permsMap = {};
      rolePerms.forEach(p => {
        permsMap[p.page_id] = {
          can_read: !!p.can_read,
          can_create: !!p.can_create,
          can_update: !!p.can_update,
          can_delete: !!p.can_delete,
        };
      });
      
      setPermissionsData(permsMap);
      setEditingPermissions(role);
      setPermissionsModalOpen(true);
    } catch (error) {
      toast.error('Error al cargar permisos del rol');
    }
  };

  const handleSave = async () => {
    if (!formData.role_name.trim()) {
      toast.error('El nombre del rol es requerido');
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/admin/roles/${editingItem.id}`, formData);
        toast.success('Rol actualizado exitosamente');
      } else {
        await api.post('/admin/roles', formData);
        toast.success('Rol creado exitosamente');
      }
      setModalOpen(false);
      fetchRoles();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al guardar';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!editingPermissions) return;

    setSaving(true);
    try {
      // Convert map to array
      const permissions = Object.entries(permissionsData).map(([pageId, perms]) => ({
        page_id: parseInt(pageId),
        can_read: perms.can_read,
        can_create: perms.can_create,
        can_update: perms.can_update,
        can_delete: perms.can_delete,
      }));

      await api.put(`/admin/roles/${editingPermissions.id}/permissions`, {
        role_id: editingPermissions.id,
        permissions,
      });
      
      toast.success('Permisos actualizados exitosamente');
      setPermissionsModalOpen(false);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al guardar permisos';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    try {
      await api.delete(`/admin/roles/${role.id}`);
      toast.success('Rol eliminado exitosamente');
      setDeleteConfirm(null);
      fetchRoles();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al eliminar rol';
      toast.error(msg);
    }
  };

  const handleCloneRole = async (role) => {
    try {
      // 1. Obtener permisos del rol original
      const response = await api.get(`/admin/roles/${role.id}`);
      const originalPerms = response.data.permissions || [];
      
      // 2. Crear el nuevo rol con nombre único
      const timestamp = Date.now().toString().slice(-4);
      const newRoleData = {
        role_name: `${role.role_name}_copia_${timestamp}`,
        description: `Copia de ${role.description || role.role_name}`,
        is_active: role.is_active,
      };
      
      const createResponse = await api.post('/admin/roles', newRoleData);
      const newRoleId = createResponse.data.id;
      
      // 3. Copiar los permisos al nuevo rol
      if (originalPerms.length > 0) {
        const permissions = originalPerms.map(p => ({
          page_id: p.page_id,
          can_read: p.can_read ? 1 : 0,
          can_create: p.can_create ? 1 : 0,
          can_update: p.can_update ? 1 : 0,
          can_delete: p.can_delete ? 1 : 0,
        }));
        
        await api.put(`/admin/roles/${newRoleId}/permissions`, {
          role_id: newRoleId,
          permissions,
        });
      }
      
      toast.success('Rol clonado exitosamente');
      fetchRoles();
      
      // Abrir modal de edición para el nuevo rol
      const newRole = { ...role, id: newRoleId, role_name: newRoleData.role_name, description: newRoleData.description };
      handleEdit(newRole);
      
    } catch (error) {
      let msg = 'Error al clonar rol';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        msg = Array.isArray(detail) ? detail[0]?.msg || msg : detail;
      }
      toast.error(msg);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePermissionChange = (pageId, action, value) => {
    setPermissionsData(prev => ({
      ...prev,
      [pageId]: {
        ...(prev[pageId] || { can_read: false, can_create: false, can_update: false, can_delete: false }),
        [action]: value,
      }
    }));
  };

  const handleToggleAll = (pageId, value) => {
    setPermissionsData(prev => ({
      ...prev,
      [pageId]: {
        can_read: value,
        can_create: value,
        can_update: value,
        can_delete: value,
      }
    }));
  };

  const handleModuleToggleAll = (moduleName, value) => {
    const modulePages = modules[moduleName] || [];
    const newPerms = { ...permissionsData };
    modulePages.forEach(page => {
      newPerms[page.id] = {
        can_read: value,
        can_create: value,
        can_update: value,
        can_delete: value,
      };
    });
    setPermissionsData(newPerms);
  };

  // Toggle una columna específica de permisos para un módulo
  const handleColumnToggle = (moduleName, action) => {
    const modulePages = modules[moduleName] || [];
    const newPerms = { ...permissionsData };
    
    // Verificar si todos están marcados actualmente
    const allChecked = modulePages.every(page => {
      const perms = newPerms[page.id] || {};
      return perms[action];
    });
    
    // Si todos están marcados, desmarcar todos. Si no, marcar todos.
    const newValue = !allChecked;
    
    modulePages.forEach(page => {
      newPerms[page.id] = {
        ...(newPerms[page.id] || { can_read: false, can_create: false, can_update: false, can_delete: false }),
        [action]: newValue,
      };
    });
    setPermissionsData(newPerms);
  };

  // Verificar si toda una columna está marcada
  const isColumnAllChecked = (moduleName, action) => {
    const modulePages = modules[moduleName] || [];
    if (modulePages.length === 0) return false;
    return modulePages.every(page => {
      const perms = permissionsData[page.id] || {};
      return perms[action];
    });
  };

  const toggleModule = (moduleName) => {
    setExpandedModules(prev => ({ ...prev, [moduleName]: !prev[moduleName] }));
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Roles</h1>
            <p className="page-subtitle">Gestión de roles y permisos del sistema</p>
          </div>
          <Button className="btn btn-primary" onClick={handleCreate} data-testid="add-role-btn">
            <Plus className="h-4 w-4" />
            Nuevo Rol
          </Button>
        </div>

        {/* Roles List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
            </div>
          ) : roles.length === 0 ? (
            <div className="col-span-full">
              <Card className="card-subtle">
                <CardContent className="py-12 text-center">
                  <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No hay roles definidos</p>
                </CardContent>
              </Card>
            </div>
          ) : roles.map((role) => (
            <Card key={role.id} className="card-subtle" data-testid={`role-card-${role.role_name}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <Shield className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{role.role_name}</CardTitle>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {role.user_count || 0} usuario(s)
                      </p>
                    </div>
                  </div>
                  <span className={`badge ${role.is_active ? 'badge-success' : 'badge-error'}`}>
                    {role.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4 min-h-[40px]">
                  {role.description || 'Sin descripción'}
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleEditPermissions(role)}
                    data-testid={`edit-permissions-${role.role_name}`}
                  >
                    <Shield className="h-3.5 w-3.5 mr-1" />
                    Permisos
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleCloneRole(role)}
                    title="Clonar rol"
                    data-testid={`clone-role-${role.role_name}`}
                  >
                    <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleEdit(role)}
                    data-testid={`edit-role-${role.role_name}`}
                  >
                    <Edit className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeleteConfirm(role)}
                    disabled={role.id === 1}
                    data-testid={`delete-role-${role.role_name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Create/Edit Role Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="modal-content max-w-md">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar Rol' : 'Nuevo Rol'}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4 space-y-4">
              <div>
                <Label className="text-sm font-medium">Nombre del Rol *</Label>
                <Input 
                  value={formData.role_name} 
                  onChange={(e) => handleInputChange('role_name', e.target.value)} 
                  className="input-subtle mt-1.5" 
                  placeholder="ej: operator"
                  data-testid="input-role-name"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Descripción</Label>
                <Input 
                  value={formData.description} 
                  onChange={(e) => handleInputChange('description', e.target.value)} 
                  className="input-subtle mt-1.5" 
                  placeholder="Descripción del rol..."
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Rol Activo</Label>
                <Switch 
                  checked={formData.is_active === 1}
                  onCheckedChange={(checked) => handleInputChange('is_active', checked ? 1 : 0)}
                />
              </div>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
              <Button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Permissions Matrix Modal */}
        <Dialog open={permissionsModalOpen} onOpenChange={setPermissionsModalOpen}>
          <DialogContent className="modal-content max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Permisos: {editingPermissions?.role_name}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4 overflow-y-auto flex-1">
              <div className="space-y-4">
                {Object.entries(modules).map(([moduleName, modulePages]) => {
                  const ModuleIcon = moduleIcons[moduleName] || Shield;
                  const isExpanded = expandedModules[moduleName];
                  
                  return (
                    <div key={moduleName} className="border border-border/30 rounded-lg overflow-hidden">
                      {/* Module Header */}
                      <div 
                        className="flex items-center justify-between px-4 py-3 bg-muted/30 cursor-pointer"
                        onClick={() => toggleModule(moduleName)}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                          <ModuleIcon className="h-4 w-4 text-primary" />
                          <span className="font-medium">{moduleNames[moduleName] || moduleName}</span>
                          <span className="text-xs text-muted-foreground">({modulePages.length} páginas)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 text-xs"
                            onClick={(e) => { e.stopPropagation(); handleModuleToggleAll(moduleName, true); }}
                          >
                            Todos
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 text-xs"
                            onClick={(e) => { e.stopPropagation(); handleModuleToggleAll(moduleName, false); }}
                          >
                            Ninguno
                          </Button>
                        </div>
                      </div>
                      
                      {/* Module Pages */}
                      {isExpanded && (
                        <div className="divide-y divide-border/20">
                          {/* Header row - clickeable */}
                          <div className="grid grid-cols-6 gap-2 px-4 py-2 bg-muted/10 text-xs font-medium text-muted-foreground">
                            <div className="col-span-2">Página</div>
                            <div 
                              className="text-center cursor-pointer hover:text-primary transition-colors select-none"
                              onClick={() => handleColumnToggle(moduleName, 'can_read')}
                              title="Click para marcar/desmarcar todos"
                            >
                              <span className={isColumnAllChecked(moduleName, 'can_read') ? 'text-primary' : ''}>
                                Leer
                              </span>
                            </div>
                            <div 
                              className="text-center cursor-pointer hover:text-primary transition-colors select-none"
                              onClick={() => handleColumnToggle(moduleName, 'can_create')}
                              title="Click para marcar/desmarcar todos"
                            >
                              <span className={isColumnAllChecked(moduleName, 'can_create') ? 'text-primary' : ''}>
                                Crear
                              </span>
                            </div>
                            <div 
                              className="text-center cursor-pointer hover:text-primary transition-colors select-none"
                              onClick={() => handleColumnToggle(moduleName, 'can_update')}
                              title="Click para marcar/desmarcar todos"
                            >
                              <span className={isColumnAllChecked(moduleName, 'can_update') ? 'text-primary' : ''}>
                                Editar
                              </span>
                            </div>
                            <div 
                              className="text-center cursor-pointer hover:text-primary transition-colors select-none"
                              onClick={() => handleColumnToggle(moduleName, 'can_delete')}
                              title="Click para marcar/desmarcar todos"
                            >
                              <span className={isColumnAllChecked(moduleName, 'can_delete') ? 'text-primary' : ''}>
                                Eliminar
                              </span>
                            </div>
                          </div>
                          
                          {modulePages.map((page) => {
                            const perms = permissionsData[page.id] || {};
                            return (
                              <div key={page.id} className="grid grid-cols-6 gap-2 px-4 py-2.5 items-center hover:bg-muted/5">
                                <div className="col-span-2 text-sm">{page.page_name}</div>
                                <div className="flex justify-center">
                                  <Checkbox 
                                    checked={perms.can_read || false}
                                    onCheckedChange={(v) => handlePermissionChange(page.id, 'can_read', v)}
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <Checkbox 
                                    checked={perms.can_create || false}
                                    onCheckedChange={(v) => handlePermissionChange(page.id, 'can_create', v)}
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <Checkbox 
                                    checked={perms.can_update || false}
                                    onCheckedChange={(v) => handlePermissionChange(page.id, 'can_update', v)}
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <Checkbox 
                                    checked={perms.can_delete || false}
                                    onCheckedChange={(v) => handlePermissionChange(page.id, 'can_delete', v)}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <DialogFooter className="modal-footer border-t">
              <Button variant="outline" onClick={() => setPermissionsModalOpen(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button className="btn btn-primary" onClick={handleSavePermissions} disabled={saving}>
                <Save className="h-4 w-4 mr-1" />
                {saving ? 'Guardando...' : 'Guardar Permisos'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4">
              <p className="text-muted-foreground">
                ¿Está seguro que desea eliminar el rol <span className="font-semibold text-foreground">{deleteConfirm?.role_name}</span>?
              </p>
              {deleteConfirm?.user_count > 0 && (
                <p className="text-sm text-destructive mt-2">
                  Este rol tiene {deleteConfirm.user_count} usuario(s) asignado(s). Debe reasignarlos antes de eliminar.
                </p>
              )}
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default RolesPage;
